/**
 * Proactive engagement cap/dismiss evaluator (doc 116 §2.4, D3).
 * Trigger wiring is TODO (CO2); cap and dismiss logic is real and unit-tested.
 */

import type { CompanionProactiveCaps, CompanionProactiveTrigger } from './types';
import { DEFAULT_COMPANION_PROACTIVE_CAPS } from './types';

export const COMPANION_PROACTIVE_DISMISS_STORAGE_KEY = 'camlin-companion-proactive-dismiss-v1';

export interface CompanionProactiveDismissRecord {
  dismissedAt: string;
}

export interface CompanionProactiveStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CompanionProactiveSessionState {
  sessionId: string;
  nudgesShownThisSession: number;
  nudgesShownByPage: Record<string, number>;
}

export interface CompanionProactiveEvaluationInput {
  pagePath: string;
  caps?: CompanionProactiveCaps;
  session: CompanionProactiveSessionState;
  storage: CompanionProactiveStorage;
  now?: Date;
  /** When true, a matching trigger fired (CO2 will wire real detectors). */
  triggerMatched?: boolean;
  triggers?: CompanionProactiveTrigger[];
}

export type CompanionProactiveBlockReason =
  | 'proactive_disabled'
  | 'dismiss_cooloff'
  | 'page_cap'
  | 'session_cap'
  | 'no_trigger';

export interface CompanionProactiveEvaluationResult {
  allowed: boolean;
  reason?: CompanionProactiveBlockReason;
  caps: CompanionProactiveCaps;
}

function daysBetween(from: Date, to: Date): number {
  return (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
}

export function readProactiveDismissRecord(
  storage: CompanionProactiveStorage
): CompanionProactiveDismissRecord | null {
  const raw = storage.getItem(COMPANION_PROACTIVE_DISMISS_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CompanionProactiveDismissRecord;
    if (typeof parsed.dismissedAt === 'string') {
      return parsed;
    }
  } catch {
    // ignore
  }

  return null;
}

export function recordProactiveDismiss(
  storage: CompanionProactiveStorage,
  now: Date = new Date()
): CompanionProactiveDismissRecord {
  const record: CompanionProactiveDismissRecord = { dismissedAt: now.toISOString() };
  storage.setItem(COMPANION_PROACTIVE_DISMISS_STORAGE_KEY, JSON.stringify(record));
  return record;
}

export function isProactiveDismissCoolOffActive(
  storage: CompanionProactiveStorage,
  coolOffDays: number,
  now: Date = new Date()
): boolean {
  const dismiss = readProactiveDismissRecord(storage);
  if (!dismiss) {
    return false;
  }

  const dismissedAt = new Date(dismiss.dismissedAt);
  if (Number.isNaN(dismissedAt.getTime())) {
    return false;
  }

  return daysBetween(dismissedAt, now) < coolOffDays;
}

export function createProactiveSessionState(sessionId?: string): CompanionProactiveSessionState {
  const id =
    sessionId ||
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `session-${Date.now()}`);

  return {
    sessionId: id,
    nudgesShownThisSession: 0,
    nudgesShownByPage: {}
  };
}

export function recordProactiveNudgeShown(
  session: CompanionProactiveSessionState,
  pagePath: string
): CompanionProactiveSessionState {
  const pageCount = session.nudgesShownByPage[pagePath] || 0;
  return {
    ...session,
    nudgesShownThisSession: session.nudgesShownThisSession + 1,
    nudgesShownByPage: {
      ...session.nudgesShownByPage,
      [pagePath]: pageCount + 1
    }
  };
}

/**
 * Evaluate whether a proactive nudge may be shown under D3 conservative caps.
 * CO2 will call this after real trigger detectors set triggerMatched=true.
 */
export function evaluateProactiveNudge(
  input: CompanionProactiveEvaluationInput
): CompanionProactiveEvaluationResult {
  const caps = input.caps || DEFAULT_COMPANION_PROACTIVE_CAPS;
  const now = input.now || new Date();

  if (!caps.enabled) {
    return { allowed: false, reason: 'proactive_disabled', caps };
  }

  if (isProactiveDismissCoolOffActive(input.storage, caps.dismissCoolOffDays, now)) {
    return { allowed: false, reason: 'dismiss_cooloff', caps };
  }

  const pageCount = input.session.nudgesShownByPage[input.pagePath] || 0;
  if (pageCount >= caps.maxNudgesPerPage) {
    return { allowed: false, reason: 'page_cap', caps };
  }

  if (input.session.nudgesShownThisSession >= caps.maxNudgesPerSession) {
    return { allowed: false, reason: 'session_cap', caps };
  }

  if (!input.triggerMatched) {
    return { allowed: false, reason: 'no_trigger', caps };
  }

  return { allowed: true, caps };
}

/** Skeleton trigger matcher — CO2 replaces with dwell/scroll/exit detectors. */
export function matchProactiveTrigger(
  triggers: CompanionProactiveTrigger[],
  context: { pagePath: string; scrollFraction?: number; dwellSeconds?: number }
): CompanionProactiveTrigger | null {
  for (const trigger of triggers) {
    if (trigger.kind === 'page_dwell' && context.pagePath.includes(trigger.match)) {
      const required = trigger.dwellSeconds ?? 30;
      if ((context.dwellSeconds ?? 0) >= required) {
        return trigger;
      }
    }

    if (trigger.kind === 'scroll_depth' && context.pagePath.includes(trigger.match)) {
      const required = trigger.scrollFraction ?? 0.5;
      if ((context.scrollFraction ?? 0) >= required) {
        return trigger;
      }
    }

    if (trigger.kind === 'exit_intent' && trigger.match === context.pagePath) {
      return trigger;
    }
  }

  return null;
}
